import React, { useState, useRef, FormEvent } from 'react'
import { ConditionallyRender } from 'components'
import {
  Button,
  Stepper,
  StepLabel,
  Step,
  Tooltip,
  Box,
  SelectChangeEvent,
} from '@mui/material'
import searchSuggestions from 'services/cm-api/get-search-suggestions'
import { customerTypeahead } from 'services/cm-api/customer-typeahead'
import CreateCompany from './create-company'
import CreateUsers from './create-users'

import { useIntl } from 'react-intl'

import { normalizeLocalId, validateLocalId } from 'services/helpers/local-id'
import { useUserAuth } from 'contexts/google-auth'
import { useCreateCustomer } from 'services/queries/use-create-customer'
import { AvailableCountriesT, SubscriptionPlan } from 'types'

interface NewCustomerProps {
  closeFn: () => void
  risikaUsers?: Array<{ id: number | string; email: string; name?: string }>
}

interface CreateCustomerState {
  country: AvailableCountriesT
  local_id: string
  name: string
  emails: string
  plan: SubscriptionPlan
  responsible_user_id: string | number | undefined
}

interface NewUser {
  userName: string
  email: string
}

// The customer the entered CVR/org-nr already belongs to, if any. CMAPI
// rejects a duplicate with a 409, but the operator should know before they
// fill in a second step's worth of user details (ENG2-2325).
interface ExistingCustomer {
  id: number
  name: string
  local_id: string
  country: string
}

const CreateCustomerDialog = ({
  closeFn,
  risikaUsers = [],
}: NewCustomerProps) => {
  const { messages } = useIntl()
  const { user } = useUserAuth()
  const currentRisikaUserId = risikaUsers?.find(
    (risikaUser) => risikaUser.email.split('.')[0] === user.email.split('.')[0],
  )?.id

  const [activeStep, setActiveStep] = useState(0)

  const responsibleOptions = risikaUsers.map((risikaUser) => {
    return {
      text: `${risikaUser.email} | ${risikaUser.name}`,
      value: risikaUser.id,
    }
  })

  const initialCreateCustomerState: CreateCustomerState = {
    country: 'DK',
    local_id: '',
    name: '',
    emails: '',
    plan: 'TRIAL_V2',
    responsible_user_id: currentRisikaUserId,
  }
  const defaultValidate = {
    local_id: false,
  }
  const [validate, setValidate] = useState(defaultValidate)
  const [createCustomerState, setCreateCustomerState] = useState(
    initialCreateCustomerState,
  )
  const [newUsers, setNewUsers] = useState<NewUser[]>([
    { userName: '', email: '' },
  ])
  const [validateUsers, setValidateUsers] = useState(false)
  const [existingCustomer, setExistingCustomer] =
    useState<ExistingCustomer | null>(null)
  // True while the registry and duplicate lookups for the number currently in
  // the field are still in flight. Without it, Next was briefly enabled while
  // the *previous* number's results were still on screen (ENG2-2328).
  const [isLookingUpLocalId, setIsLookingUpLocalId] = useState(false)
  // Every keystroke starts a new pair of lookups. Responses can land out of
  // order, so each one carries the sequence number it was issued with and is
  // discarded unless it is still the newest.
  const lookupSequence = useRef(0)
  const { mutate: createCustomer, isLoading: isCreating } = useCreateCustomer()

  const createNewCustomer = async (event: FormEvent) => {
    event.preventDefault()

    const { local_id, name, plan, responsible_user_id, country } =
      createCustomerState

    const userData =
      newUsers.length === 1 && newUsers[0].email === ''
        ? []
        : newUsers.map((user) => {
            return {
              name: user.userName,
              email: user.email,
              phone_number: 'default',
            }
          })

    const dataBody = {
      local_organization_id: {
        id: local_id,
        country,
      },
      name,
      subscription_plan: plan,
      responsible_user_id: Number(responsible_user_id),
      userData,
    }

    // Close only once the customer actually exists. Closing first meant a
    // failed create looked identical to a successful one — the operator lost
    // the form, saw a generic error, and retried into a second duplicate
    // (ENG2-2325).
    createCustomer(dataBody, { onSuccess: () => closeFn() })
  }

  const handleCreateCustomerChange =
    (type: string) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | { value: unknown }>
        | SelectChangeEvent<string | number>,
    ) => {
      const value = (event.target as HTMLInputElement).value
      switch (type) {
        case 'local_id':
          {
            // Store what we validate. The raw value used to be persisted, so a
            // pasted "8755 2411" became a second row for a company already
            // registered as "87552411" (ENG2-2325).
            const localId = normalizeLocalId(value)
            const country = createCustomerState.country ?? 'DK'
            const isLocalIdValid = validateLocalId({
              id: localId,
              country,
            })
            setValidate({
              ...validate,
              [type]: isLocalIdValid,
            })

            // Everything on screen describes the *previous* number, so drop it
            // all now rather than leaving it until the new lookups answer. The
            // name in particular used to survive a lookup that found nothing,
            // so a customer could be created under the last company's name
            // (ENG2-2328).
            lookupSequence.current += 1
            const sequence = lookupSequence.current
            const isCurrent = () => lookupSequence.current === sequence

            setExistingCustomer(null)
            setCreateCustomerState({
              ...createCustomerState,
              name: '',
              local_id: localId,
            })

            if (!isLocalIdValid) {
              setIsLookingUpLocalId(false)
              return
            }

            setIsLookingUpLocalId(true)

            const registryLookup = searchSuggestions(
              localId,
              createCustomerState.country,
            )
              .then((data: any) => {
                if (!isCurrent()) return
                const { name } = data[0]
                setCreateCustomerState((current) => ({ ...current, name }))
              })
              .catch(() => {
                // No registry match — the name field stays empty rather than
                // keeping whatever the last number resolved to.
              })

            // Tell the operator up front if this company is already a
            // customer, rather than letting them fill in step 2 and take a
            // 409 from CMAPI.
            const duplicateLookup = customerTypeahead(localId)
              .then(({ data }) => {
                if (!isCurrent()) return
                const match = data?.find(
                  (customer) =>
                    normalizeLocalId(customer.local_id) === localId &&
                    customer.country === country,
                )
                setExistingCustomer((match as ExistingCustomer) ?? null)
              })
              .catch(() => {
                // Best-effort only — CMAPI still rejects the duplicate.
              })

            Promise.all([registryLookup, duplicateLookup]).then(() => {
              if (isCurrent()) setIsLookingUpLocalId(false)
            })
          }

          return
        case 'name':
          setCreateCustomerState({ ...createCustomerState, name: value })
          return
        case 'country':
          setCreateCustomerState({
            ...createCustomerState,
            country: value as AvailableCountriesT,
          })
          return
        case 'plan':
          setCreateCustomerState({
            ...createCustomerState,
            plan: value as SubscriptionPlan,
          })
          return
        case 'emails':
          setCreateCustomerState({ ...createCustomerState, emails: value })
          return
        case 'responsible_user_id':
          setCreateCustomerState({
            ...createCustomerState,
            responsible_user_id: value,
          })
          return
        default:
          return null
      }
    }

  function getSteps() {
    return ['Create company', 'Create users']
  }

  function getStepContent(step: number) {
    switch (step) {
      case 0:
        return (
          <CreateCompany
            handleCreateCustomerChange={handleCreateCustomerChange}
            createCustomerState={createCustomerState}
            messages={messages}
            responsibleOptions={responsibleOptions}
            validate={validate}
            existingCustomer={existingCustomer}
          />
        )
      case 1:
        return (
          <CreateUsers
            handleCreateCustomerChange={handleCreateCustomerChange}
            createCustomerState={createCustomerState}
            messages={messages}
            responsibleOptions={responsibleOptions}
            users={newUsers}
            setUsers={setNewUsers}
            validateUsers={validateUsers}
            setValidateUsers={setValidateUsers}
          />
        )
      default:
        return 'Unknown step'
    }
  }

  // Why step 1 can't be left yet, or '' when it can. Nested ternaries in the
  // Tooltip made this hard to follow once the pending state was added.
  const nextStepBlockedReason = (() => {
    if (!validate.local_id) {
      return 'Please enter a valid CVR/Org number to continue'
    }
    if (isLookingUpLocalId) {
      return 'Looking up the company…'
    }
    if (existingCustomer) {
      return `${existingCustomer.name} is already customer ${existingCustomer.id}`
    }
    return ''
  })()

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  return (
    <form onSubmit={createNewCustomer}>
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {getSteps().map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ px: 3, pb: 2 }}>{getStepContent(activeStep)}</Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        <Button
          variant="outlined"
          size="small"
          disabled={activeStep === 0}
          onClick={handleBack}
          sx={{ minWidth: 80 }}
        >
          Back
        </Button>

        <ConditionallyRender
          condition={activeStep === 0}
          when={
            <Tooltip title={nextStepBlockedReason} placement="top">
              <span>
                <Button
                  disabled={
                    !validate.local_id ||
                    Boolean(existingCustomer) ||
                    isLookingUpLocalId
                  }
                  variant="contained"
                  size="small"
                  onClick={handleNext}
                  sx={{ minWidth: 80 }}
                >
                  Next
                </Button>
              </span>
            </Tooltip>
          }
          otherwise={
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={!validateUsers || isCreating}
              sx={{ minWidth: 80 }}
            >
              {isCreating ? 'Creating…' : 'Create'}
            </Button>
          }
        />
      </Box>
    </form>
  )
}

export default CreateCustomerDialog
